'use client';

import type { Translate } from 'next-translate';
import { useEffect, useMemo, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { WalletInstitution } from '@/modules/Wallet/interface/walet';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useGetCustomerAdvisor } from '@/modules/Customer/http/client/useGetCustomerAdvisor';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { useGetWalletInstitutions } from '@/modules/Wallet/http/client/useGetWalletInstitutions';
import { INVESTMENT_ORDER_TYPES_TRANSLATE, type InvestmentOrder } from '@/repositories/invest/InvestRepository.types';
import { useInvest } from '@/contexts/InvestContext';
import { investStep, MODAL_INVEST_ID } from './utils';
import { formatNumberToPTBRCurrency } from '@/utils/format';
import { formatAdvisorWhatsAppLink } from '@/utils/messenger';
export const useInvestViewModel = () => {
  const investmentModeViewRef = useRef(investStep[0]);
  const {
    t
  } = useTranslation('common');
  const {
    selectAsset
  } = useInvest();
  const {
    data: customerStatus
  } = useGetCustomerStatus();
  const {
    data: advisor,
    isLoading
  } = useGetCustomerAdvisor();
  const {
    data: {
      data: institutions
    } = {
      data: [],
      total: 0
    }
  } = useGetWalletInstitutions();
  const {
    mutate: createSFOpportunity
  } = useCreateSFOpportunity();
  const [investmentModeView, setInvestmentModeView] = useState(investStep[0]);
  const [investmentOrder, setInvestmentOrder] = useState<InvestmentOrder>();
  const hasAdvisor = !!customerStatus?.isEqi && !customerStatus.isEqiDigital;
  const handleBackModeInvestView = () => {
    if (investmentModeViewRef.current === investStep[0]) {
      window.dispatchEvent(new Event(`close-chakra-modal-${MODAL_INVEST_ID}`));
      return;
    }
    setInvestmentModeView(investStep[0]);
    investmentModeViewRef.current = investStep[0];
  };
  const setInvestStep = async (mode: string) => {
    setInvestmentModeView(mode);
    investmentModeViewRef.current = mode;
  };
  const setOrderToSend = async (data: InvestmentOrder) => {
    setInvestmentOrder(data);
    setInvestmentModeView(investStep[3]);
    investmentModeViewRef.current = investStep[3];
  };
  const sendOrder = async () => {
    if (!investmentOrder) return;
    const type = INVESTMENT_ORDER_TYPES_TRANSLATE[investmentOrder.type].toUpperCase();
    const amount = formatNumberToPTBRCurrency(Number(investmentOrder?.amount));
    const assetTitle = getAssetTitle(investmentOrder);
    const ticker = 'ticker' in investmentOrder.asset ? investmentOrder.asset.ticker : '';
    const messageHasAdvisor = `\n\n Olá, ${advisor?.advisorName}, gostaria de ${type} a quantia de ${amount} no ativo ${assetTitle} (${ticker}), pode me ajudar a executar?`;
    const messageHasNotAdvisor = `\n\n Olá, gostaria de ${type} a quantia de ${amount} no ativo ${assetTitle} (${ticker}), pode me ajudar a executar?`;
    const finalMessage = hasAdvisor ? messageHasAdvisor : messageHasNotAdvisor;
    hasAdvisor && createSFOpportunity({
      action: salesforceOpportunityConfig.banking.invest.action,
      product: ticker,
      amount,
      rawQuestions: {
        amount
      },
      translate: () => type
    });
    const url = formatAdvisorWhatsAppLink({
      message: finalMessage,
      number: advisor?.advisorMobilePhone ?? ''
    });
    window.open(url, '_blank');
    setInvestmentModeView(investStep[0]);
    investmentModeViewRef.current = investStep[0];
  };
  const walletBalances: WalletBalances = useMemo(() => calculateBalances(institutions, t),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [institutions]);
  useEffect(() => {
    window.addEventListener(`action-back-button-drawer-${MODAL_INVEST_ID}`, handleBackModeInvestView);
    return () => {
      window.removeEventListener(`action-back-button-drawer-${MODAL_INVEST_ID}`, handleBackModeInvestView);
      selectAsset({
        asset: null,
        scrollUp: true
      });
      setInvestmentOrder(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    advisor,
    investmentModeView,
    isEqiDigital: customerStatus?.isEqiDigital,
    isLoading,
    walletBalances,
    order: investmentOrder,
    sendOrder,
    setInvestStep,
    setOrderToSend
  };
};
const calculateBalances = (institutions: WalletInstitution[], t: Translate): WalletBalances => {
  if (!institutions) {
    return {
      total: 0,
      balances: []
    };
  }
  const total = institutions.reduce((acc, {
    checking_account_total
  }) => acc + checking_account_total, 0);
  const totalEqi = institutions.find(({
    name
  }) => name.toLowerCase() === 'eqi')?.checking_account_total ?? 0;
  return {
    total,
    balances: [{
      label: t('screens.invest.balance.eqi'),
      value: totalEqi
    }, {
      label: t('screens.invest.balance.others'),
      value: total - totalEqi
    }]
  };
};
const getAssetTitle = (orderDataToSend: InvestmentOrder) => {
  if ('title' in orderDataToSend.asset) {
    return orderDataToSend.asset.title;
  }
  return orderDataToSend.asset.name;
};
type WalletBalances = {
  total: number;
  balances: {
    label: string;
    value: number;
  }[];
};